// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XPopup from "@gcg/App/Components/XPopup/XPopup";
import { UnitOfMeasureData } from "@gcg/Overrides/Components/ProductQuantityOrdered";
import color from "@gcg/XDL/Utilities/color";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    HasProduct,
    HasProductContext,
    withProduct,
    withProductContext,
} from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductDetailsPageContext } from "@insite/content-library/Pages/ProductDetailsPage";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import LazyImage from "@insite/mobius/LazyImage/LazyImage";
import Popover, { ContentBodyProps, OverflowWrapperProps, PopoverPresentationProps } from "@insite/mobius/Popover";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    cutWireSettings: getSettingsCollection(state).cutWireSettings,
});

type ProductBadge = {
    DisplayName: string;
    Description: string;
    Image: string;
};

type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps> & HasProductContext;

export interface ProductDetailsPartNumberStyles {
    qtyInfoContainer?: InjectableCss;
    badgesContainer?: InjectableCss;
    badgeImage?: LazyImageProps;
    badgePopover?: PopoverPresentationProps;
    popoverWrapper?: OverflowWrapperProps;
    popoverContentBody?: ContentBodyProps;
    labelText?: TypographyProps;
    valueText?: TypographyProps;
    packDescription?: InjectableCss;
    cutLengthNotification?: TypographyProps;
}

export const partNumberStyles: ProductDetailsPartNumberStyles = {
    qtyInfoContainer: {
        css: css`
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            margin: 20px 0;
        `,
    },
    badgesContainer: {
        css: css`
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            margin: 20px 0;
        `,
    },
    badgeImage: {
        width: "20px",
        height: "20px",
        css: css`
            cursor: pointer;
            margin-right: 10px;

            img {
                width: auto;
                height: auto;
                max-width: 100%;
                max-height: 100%;
            }
        `,
    },
    badgePopover: {
        transitionDuration: "short",
        toggle: false,
        xPosition: "middle",
        yPosition: "top",
    },
    popoverWrapper: {
        as: "div",
        _height: "auto",
        css: css`
            position: relative;
        `,
    },
    popoverContentBody: {
        as: "div",
        _height: "100%",
        css: css`
            max-width: 320px;
            font-size: 12px;
            letter-spacing: 0;
            line-height: 18px;
            padding: 15px;
            box-shadow: 0 4px 8px 0 ${color("common.backgroundContrast", 0.2)};
        `,
    },
    cutLengthNotification: {
        size: "15px",
        css: css`
            display: flex;
            align-items: center;
            width: 100%;
            margin-top: 15px;
        `,
    },
    labelText: {
        size: 12,
        lineHeight: "18px",
        weight: "bold",
        css: css`
            margin-right: 2px;
        `,
    },
    valueText: {
        size: 12,
        lineHeight: "18px",
        css: css``,
    },
    packDescription: {
        css: css`
            flex: 1 1 100%;
        `,
    },
};

const styles = partNumberStyles;

const ProductDetailsBadges: React.FC<Props> = ({
    product,
    productContext: {
        productInfo: { unitOfMeasure },
    },
}) => {
    if (product?.isVariantParent) {
        return null;
    }
    const unitOfMeasuresData: UnitOfMeasureData[] = JSON.parse(product.properties.unitOfMeasureData || "[]");
    const [minQty, setMinQty] = React.useState<string | number>(1);
    const [msq, SetMsq] = React.useState<string | number>(1);
    React.useEffect(() => {
        if (unitOfMeasuresData && unitOfMeasure) {
            const selectedUOM = unitOfMeasuresData.filter(uom => uom.UnitOfMeasure === unitOfMeasure);
            if (selectedUOM.length) {
                setMinQty(selectedUOM[0].MinimumOrderQuantity || 1);
                SetMsq(selectedUOM[0].MultipleSaleQuantity || 1);
            } else {
                setMinQty(product.minimumOrderQty || 1);
                SetMsq(product.detail?.multipleSaleQty || 1);
            }
        }
    }, [unitOfMeasure]);

    let badges: ProductBadge[] = [];
    if (product?.properties?.badges) {
        badges = product?.properties?.badges && JSON.parse(product.properties["badges"]);
        if (badges?.length > 0) {
            badges.every(badge => {
                if (badge.DisplayName === "PROP_65_CANCER") {
                    badge.Description =
                        product.properties.prop65Text || (siteMessage("Custom_Prop65DefaultText") as string);
                }
                return true;
            });
        }
    }

    return (
        <>
            <StyledWrapper {...styles.qtyInfoContainer}>
                <Typography {...styles.valueText} as="p">
                    <Typography {...styles.labelText}>{translate("Min Qty:")}</Typography>
                    <Typography>{minQty}</Typography>
                </Typography>
                &nbsp;&nbsp;|&nbsp;&nbsp;
                <Typography {...styles.valueText} as="p">
                    <Typography {...styles.labelText}>{translate("Qty Increment:")}</Typography>
                    <Typography>{msq}</Typography>
                </Typography>
                {product.packDescription && (
                    <StyledWrapper {...styles.packDescription}>
                        <Typography {...styles.valueText} as="p">
                            <Typography {...styles.labelText}>{translate("Pack:")}</Typography>
                            <Typography>{product.packDescription}</Typography>
                        </Typography>
                    </StyledWrapper>
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.badgesContainer}>
                {badges.map(
                    badge =>
                        badge.Image && (
                            <StyledWrapper key={badge.DisplayName}>
                                <XPopup
                                    type="tooltip"
                                    trigger={
                                        <StyledWrapper>
                                            <LazyImage
                                                src={badge.Image}
                                                altText={badge.Description}
                                                {...styles.badgeImage}
                                            />
                                        </StyledWrapper>
                                    }
                                    position="right"
                                >
                                    <StyledWrapper className="popup-content-wrapper">
                                        {/* eslint-disable react/no-danger */}
                                        <div dangerouslySetInnerHTML={{ __html: badge.Description }}></div>
                                    </StyledWrapper>
                                </XPopup>
                            </StyledWrapper>
                        ),
                )}
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withProductContext(connect(mapStateToProps)(withProduct(ProductDetailsBadges))),
    definition: {
        displayName: "Product Badges",
        group: "Product Details",
        allowedContexts: [ProductDetailsPageContext],
    },
};

export default widgetModule;
